import { createContext, useCallback, useContext, useEffect, useMemo, useReducer, useRef } from 'react';

import { useCompany } from 'context/company';
import { useUser } from 'context/user';
import { getWorkerNavigation } from './navigations/worker';
import { getManagerNavigation } from './navigations/manager';
import { NavItem } from './types';

interface NavigationState {
  activeMainNav: number;
  activeSubNav: number;
  activeSubSideNav: number;
  isMobileMenuOpen: boolean;
  hasSidebar: boolean;
  isSidebarCollapsed: boolean;
}

interface NavigationAction {
  type:
    | 'SET_MAIN_NAV'
    | 'SET_SUB_NAV'
    | 'SET_SUB_SIDE_NAV'
    | 'TOGGLE_MOBILE_MENU'
    | 'SET_HAS_SIDEBAR'
    | 'TOGGLE_SIDEBAR_COLLAPSED';
  value?: number | boolean;
}

interface NavigationProviderProps {
  children: React.ReactNode;
  hasBanner?: boolean;
}

const SIDEBAR_COLLAPSED_KEY = 'thera-sidebar-collapsed';

const initialState: NavigationState = {
  activeMainNav: -1,
  activeSubNav: -1,
  activeSubSideNav: -1,
  isMobileMenuOpen: false,
  hasSidebar: false,
  isSidebarCollapsed: false,
};

const NavigationContext = createContext<{
  state: NavigationState;
  dispatch: React.Dispatch<NavigationAction>;
  navigation: NavItem[];
  hasBanner: boolean;
}>({
  state: initialState,
  dispatch: () => undefined,
  navigation: [],
  hasBanner: false,
});

const navigationReducer = (state: NavigationState, action: NavigationAction): NavigationState => {
  switch (action.type) {
    case 'SET_MAIN_NAV':
      return {
        ...state,
        activeMainNav: action.value as number,
      };
    case 'SET_SUB_NAV':
      return {
        ...state,
        activeSubNav: action.value as number,
      };
    case 'SET_SUB_SIDE_NAV':
      return {
        ...state,
        activeSubSideNav: action.value as number,
      };
    case 'TOGGLE_MOBILE_MENU':
      return {
        ...state,
        isMobileMenuOpen: action.value as boolean,
      };
    case 'SET_HAS_SIDEBAR':
      return {
        ...state,
        hasSidebar: action.value as boolean,
      };
    case 'TOGGLE_SIDEBAR_COLLAPSED': {
      const newCollapsed = action.value !== undefined ? (action.value as boolean) : !state.isSidebarCollapsed;
      return {
        ...state,
        isSidebarCollapsed: newCollapsed,
      };
    }
    default:
      return state;
  }
};

export const NavigationProvider: React.FC<NavigationProviderProps> = ({ children, hasBanner = false }) => {
  const { user, roleType } = useUser();
  const { company } = useCompany();
  const [state, dispatch] = useReducer(navigationReducer, initialState);
  const isWorker = roleType === 'worker';
  const isUSPayrollOnboarded = Boolean(company?.w2Payroll?.onboardingFlowComplete);

  // Hydrate sidebar collapsed state from localStorage after mount to avoid SSR mismatch
  useEffect(() => {
    try {
      const stored = localStorage.getItem(SIDEBAR_COLLAPSED_KEY);
      if (stored === 'true') {
        dispatch({ type: 'TOGGLE_SIDEBAR_COLLAPSED', value: true });
      }
    } catch {
      // localStorage unavailable
    }
  }, []);

  // Sync sidebar collapsed state to localStorage (moved out of reducer to keep it pure)
  const isInitialMount = useRef(true);
  useEffect(() => {
    // Skip the initial hydration render to avoid redundant write
    if (isInitialMount.current) {
      isInitialMount.current = false;
      return;
    }
    try {
      localStorage.setItem(SIDEBAR_COLLAPSED_KEY, String(state.isSidebarCollapsed));
    } catch {
      // localStorage unavailable
    }
  }, [state.isSidebarCollapsed]);

  // Sync body scroll lock with mobile menu state (moved out of reducer to keep it pure)
  useEffect(() => {
    document.body.style.overflow = state.isMobileMenuOpen ? 'hidden' : 'auto';
  }, [state.isMobileMenuOpen]);

  // Close mobile menu when resizing to desktop
  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth >= 1024) {
        dispatch({ type: 'TOGGLE_MOBILE_MENU', value: false });
      }
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [dispatch]);
  const navigation = useMemo(
    () => (user ? (isWorker ? getWorkerNavigation() : getManagerNavigation(isUSPayrollOnboarded)) : []),
    [user, isWorker, isUSPayrollOnboarded],
  );

  return (
    <NavigationContext.Provider value={{ state, dispatch, navigation, hasBanner }}>
      {children}
    </NavigationContext.Provider>
  );
};

export const useNavigation = () => {
  const { state, dispatch, navigation, hasBanner } = useContext(NavigationContext);
  const { activeMainNav, activeSubNav, activeSubSideNav, isMobileMenuOpen, hasSidebar, isSidebarCollapsed } = state;

  const setActiveMainNav = useCallback(
    (value: number) => {
      dispatch({ type: 'SET_MAIN_NAV', value });
    },
    [dispatch],
  );
  const setActiveSubNav = useCallback(
    (value: number) => {
      dispatch({ type: 'SET_SUB_NAV', value });
    },
    [dispatch],
  );
  const setActiveSubSideNav = useCallback(
    (value: number) => {
      dispatch({ type: 'SET_SUB_SIDE_NAV', value });
    },
    [dispatch],
  );

  const toggleMobileMenu = useCallback(() => {
    // Only toggle menu on mobile devices (width < 1024px)
    if (window.innerWidth < 1024) {
      dispatch({ type: 'TOGGLE_MOBILE_MENU', value: !isMobileMenuOpen });
    }
  }, [dispatch, isMobileMenuOpen]);

  const setHasSidebar = useCallback(
    (value: boolean) => {
      dispatch({ type: 'SET_HAS_SIDEBAR', value });
    },
    [dispatch],
  );

  const toggleSidebarCollapsed = useCallback(() => {
    dispatch({ type: 'TOGGLE_SIDEBAR_COLLAPSED' });
  }, [dispatch]);

  return {
    activeMainNav,
    activeSubNav,
    activeSubSideNav,
    isMobileMenuOpen,
    hasSidebar,
    isSidebarCollapsed,
    setActiveMainNav,
    setActiveSubNav,
    setActiveSubSideNav,
    toggleMobileMenu,
    setHasSidebar,
    toggleSidebarCollapsed,
    navigation,
    hasBanner,
  };
};
